import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import {
  composeEntityVersionPath,
  getMentionInteractionKind as getSharedMentionInteractionKind,
} from "@repo/shared";
import { useConvex } from "convex/react";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { toast } from "sonner";
import type { MessageMentionReference } from "@/lib/message-mention-link";

export type MentionInteractionKind = "navigate" | "defer" | "unsupported";

type MentionResolutionResult =
  | { action: "navigate"; path: string; entityTypeKey: string; entityId: string }
  | { action: "defer"; mentionType: string }
  | { action: "unsupported"; mentionType: string }
  | { action: "missing"; mentionType: string };

export interface MentionInteractionContextValue {
  getInteractionKind: (mentionType: string) => MentionInteractionKind;
  activateMention: (reference: MessageMentionReference) => Promise<boolean>;
}

interface ProjectMentionInteractionProviderProps extends PropsWithChildren {
  projectId: Id<"projects">;
  onNavigate: (path: string) => void;
}

const MentionInteractionContext = createContext<MentionInteractionContextValue | null>(null);

export function getMentionInteractionKind(mentionType: string): MentionInteractionKind {
  return getSharedMentionInteractionKind(mentionType);
}

export function getMentionActivationTitle(options: {
  interactionKind: MentionInteractionKind;
  editable: boolean;
  enabled: boolean;
}): string | undefined {
  if (!options.enabled || options.interactionKind !== "navigate") {
    return undefined;
  }

  return "Open referenced item";
}

export function ProjectMentionInteractionProvider({
  projectId,
  onNavigate,
  children,
}: ProjectMentionInteractionProviderProps) {
  const convex = useConvex();
  const resolutionCacheRef = useRef(new Map<string, MentionResolutionResult>());

  const activateMention = useCallback(
    async (reference: MessageMentionReference) => {
      if (getMentionInteractionKind(reference.mentionType) !== "navigate") {
        return false;
      }

      const cacheKey = `${reference.mentionType}:${reference.id}`;
      let result = resolutionCacheRef.current.get(cacheKey);
      if (!result) {
        try {
          result = await convex.query(api.navigation.resolveMentionTarget, {
            projectId,
            mentionType: reference.mentionType,
            mentionId: reference.id,
          });
          resolutionCacheRef.current.set(cacheKey, result);
        } catch (error) {
          toast.error(
            error instanceof Error
              ? error.message
              : "Unable to open this reference because project navigation is misconfigured."
          );
          return false;
        }
      }

      if (result.action === "navigate") {
        // The cache is keyed on identity alone, so one resolution serves every facet of the
        // same element; the version suffix is composed onto the resolved path afterwards.
        onNavigate(composeEntityVersionPath(result.path, reference.versionIntent));
        return true;
      }

      if (result.action === "missing") {
        toast.error("The referenced item is no longer available.");
      }

      return false;
    },
    [convex, onNavigate, projectId]
  );

  const value = useMemo<MentionInteractionContextValue>(
    () => ({
      getInteractionKind: getMentionInteractionKind,
      activateMention,
    }),
    [activateMention]
  );

  return (
    <MentionInteractionContext.Provider value={value}>
      {children}
    </MentionInteractionContext.Provider>
  );
}

export function useOptionalMentionInteraction(): MentionInteractionContextValue | null {
  return useContext(MentionInteractionContext);
}

export function useMentionInteraction(): MentionInteractionContextValue {
  const value = useOptionalMentionInteraction();
  if (!value) {
    throw new Error(
      "useMentionInteraction must be used within a ProjectMentionInteractionProvider"
    );
  }
  return value;
}
